import { Download, Filter, AlertTriangle } from 'lucide-react';
import { fmtEurCompact } from '../../utils/formatUtils';
import type { ProjectStats, FilterType, TabType } from './types';

interface RentabiliteTabProps {
  projectStats: ProjectStats[];
  rentabiliteFilter: FilterType;
  setRentabiliteFilter: (f: FilterType) => void;
  rentaSearch: string;
  setRentaSearch: (s: string) => void;
  setHistoryProject: (id: string) => void;
  setActiveTab: (tab: TabType) => void;
}

export default function RentabiliteTab({
  projectStats, rentabiliteFilter, setRentabiliteFilter,
  rentaSearch, setRentaSearch, setHistoryProject, setActiveTab
}: RentabiliteTabProps) {
  // Filtrage par recherche + filtre risque côté client
  const filteredStats = projectStats.filter(p => {
    // Filtre risque côté client (le serveur charge les actifs)
    if (rentabiliteFilter === 'risque') {
      const pct = p.budgetChantier > 0 ? (p.consumedChantier / p.budgetChantier) * 100 : 0;
      if (pct <= 100) return false;
    }
    if (!rentaSearch.trim()) return true;
    const q = rentaSearch.trim().toLowerCase();
    return p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q);
  });

  // Compteurs par statut (calculés sur les stats AVANT filtre statut, après filtre recherche)
  const countAll = filteredStats.length;
  const countArchived = filteredStats.filter(p => !p.isActive).length;
  const countRisque = filteredStats.filter(p => {
    if (!p.isActive) return false;
    const totalBudget = p.budgetChantier + p.budgetBE;
    const totalConsumed = p.consumedChantier + p.consumedBE;
    return totalBudget > 0 && (totalConsumed / totalBudget) * 100 > 100;
  }).length;
  const countEnCours = filteredStats.filter(p => p.isActive).length - countRisque;

  const fmtK = fmtEurCompact;

  const getStatusInfo = (p: ProjectStats) => {
    if (!p.isActive) return { label: 'Clôturé', color: '#64748b', bg: '#f1f5f9', dot: '#94a3b8' };
    const totalBudget = p.budgetChantier + p.budgetBE;
    const totalConsumed = p.consumedChantier + p.consumedBE;
    const pct = totalBudget > 0 ? (totalConsumed / totalBudget) * 100 : 0;
    if (pct > 100) return { label: 'Risque', color: '#ef4444', bg: '#fef2f2', dot: '#ef4444' };
    return { label: 'En cours', color: '#10b981', bg: '#ecfdf5', dot: '#10b981' };
  };

  return (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    {/* BARRE DE RECHERCHE + FILTRES + EXPORT */}
    <div style={{
      background: 'white', padding: '16px 20px', borderRadius: '14px', border: '1px solid #e2e8f0',
      display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap'
    }}>
      {/* Recherche */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '8px 14px', minWidth: '220px' }}>
        <Filter size={15} color="#94a3b8" />
        <input
          type="text"
          placeholder="Rechercher une affaire..."
          value={rentaSearch}
          onChange={e => setRentaSearch(e.target.value)}
          style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '0.88rem', color: '#1e293b', width: '100%' }}
        />
      </div>

      {/* Filtres statut */}
      <div style={{ display: 'flex', background: '#f1f5f9', padding: '3px', borderRadius: '10px', gap: '2px' }}>
        {([
          { id: 'all' as FilterType, label: `Toutes (${countAll})`, color: '#1e293b', icon: null },
          { id: 'active' as FilterType, label: `En cours (${countEnCours})`, color: '#10b981', icon: null },
          { id: 'archived' as FilterType, label: `Archivés (${countArchived})`, color: '#64748b', icon: null },
          { id: 'risque' as FilterType, label: `Risque (${countRisque})`, color: '#ef4444', icon: <AlertTriangle size={13} /> },
        ]).map(f => (
          <button
            key={f.id}
            onClick={() => setRentabiliteFilter(f.id)}
            style={{
              padding: '7px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
              fontSize: '0.82rem', fontWeight: '600', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: '4px',
              background: rentabiliteFilter === f.id ? 'white' : 'transparent',
              color: rentabiliteFilter === f.id ? f.color : '#94a3b8',
              boxShadow: rentabiliteFilter === f.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
            }}
          >
            {f.icon} {f.label}
          </button>
        ))}
      </div>

      {/* Export */}
      <button
        onClick={() => {
          // Export CSV simple
          const headers = ['Code', 'Affaire', 'Statut', 'Avancement %', 'Budget Chantier (h)', 'Chantier Réel (h)', 'Budget Bureau d\'étude (h)', 'Bureau d\'étude Réel (h)', 'Achats €', 'Marge %'];
          const rows = filteredStats.map(p => {
            const totalB = p.budgetChantier + p.budgetBE;
            const totalC = p.consumedChantier + p.consumedBE;
            const pct = totalB > 0 ? (totalC / totalB) * 100 : 0;
            const st = getStatusInfo(p);
            return [p.code, p.name, st.label, pct.toFixed(1), p.budgetChantier, p.consumedChantier.toFixed(1), p.budgetBE, p.consumedBE.toFixed(1), p.totalAchats.toFixed(0), p.margePercent?.toFixed(1) ?? '—'];
          });
          const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
          const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = 'rentabilite-chantiers.csv'; a.click();
          URL.revokeObjectURL(url);
        }}
        style={{
          marginLeft: 'auto', padding: '8px 16px', background: '#f8fafc', border: '1px solid #e2e8f0',
          borderRadius: '10px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600', color: '#475569',
          display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap'
        }}
      >
        <Download size={14} /> Exporter
      </button>
    </div>

    {/* TABLEAU RENTABILITÉ */}
    <div style={{
      background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0',
      overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Affaire</th>
            <th style={{ padding: '14px 12px', textAlign: 'center', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Statut</th>
            <th style={{ padding: '14px 12px', textAlign: 'center', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Avancement</th>
            <th style={{ padding: '14px 12px', textAlign: 'center', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Budget CH</th>
            <th style={{ padding: '14px 12px', textAlign: 'center', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>CH Réel</th>
            <th style={{ padding: '14px 12px', textAlign: 'center', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Budget BE</th>
            <th style={{ padding: '14px 12px', textAlign: 'center', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>BE Réel</th>
            <th style={{ padding: '14px 12px', textAlign: 'center', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Achats</th>
            <th style={{ padding: '14px 12px', textAlign: 'center', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>CA Facturé</th>
            <th style={{ padding: '14px 12px', textAlign: 'center', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Reste à Fact.</th>
            <th style={{ padding: '14px 12px', textAlign: 'center', fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Marge</th>
          </tr>
        </thead>
        <tbody>
          {filteredStats.length === 0 && (
            <tr><td colSpan={11} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Aucune affaire trouvée</td></tr>
          )}
          {filteredStats.map((p, idx) => {
            const totalBudgetH = p.budgetChantier + p.budgetBE;
            const totalConsumedH = p.consumedChantier + p.consumedBE;
            const pctTotal = totalBudgetH > 0 ? (totalConsumedH / totalBudgetH) * 100 : 0;
            const status = getStatusInfo(p);
            const barColor = pctTotal > 100 ? '#ef4444' : pctTotal > 80 ? '#f59e0b' : '#3b82f6';

            return (
              <tr
                key={p.id}
                onClick={() => { setHistoryProject(p.id); setActiveTab('historique'); }}
                style={{
                  borderBottom: idx < filteredStats.length - 1 ? '1px solid #f1f5f9' : 'none',
                  cursor: 'pointer', transition: 'background 0.15s'
                }}
                onMouseOver={e => { e.currentTarget.style.background = '#fafbfc'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'white'; }}
              >
                {/* AFFAIRE */}
                <td style={{ padding: '16px 16px' }}>
                  <div style={{ fontWeight: '600', fontSize: '0.92rem', color: '#1e293b', marginBottom: '2px' }}>{p.name}</div>
                  <div style={{ fontSize: '0.76rem', color: '#94a3b8', fontFamily: 'monospace' }}>{p.code}</div>
                </td>

                {/* STATUT */}
                <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    fontSize: '0.78rem', fontWeight: '600', padding: '5px 12px',
                    borderRadius: '20px', background: status.bg, color: status.color, whiteSpace: 'nowrap'
                  }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: status.dot }} />
                    {status.label}
                  </span>
                </td>

                {/* AVANCEMENT — barre + % */}
                <td style={{ padding: '16px 12px', textAlign: 'center', minWidth: '140px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                    <div style={{ width: '70px', height: '7px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(pctTotal, 100)}%`, height: '100%', background: barColor, borderRadius: '4px', transition: 'width 0.3s' }} />
                    </div>
                    <span style={{ fontSize: '0.82rem', fontWeight: '700', color: barColor, minWidth: '38px' }}>
                      {pctTotal.toFixed(0)}%
                    </span>
                  </div>
                </td>

                {/* BUDGET CHANTIER */}
                <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>
                    {p.budgetChantier > 0 ? `${p.budgetChantier} h` : '—'}
                  </span>
                </td>

                {/* CHANTIER RÉEL */}
                <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                  {(() => {
                    const pctCH = p.budgetChantier > 0 ? (p.consumedChantier / p.budgetChantier) * 100 : 0;
                    return (
                      <span style={{ fontSize: '0.85rem', fontWeight: '700', color: pctCH > 100 ? '#ef4444' : '#334155' }}>
                        {p.consumedChantier > 0 ? `${p.consumedChantier.toFixed(0)} h` : '—'}
                      </span>
                    );
                  })()}
                </td>

                {/* BUDGET BUREAU D'ÉTUDE */}
                <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>
                    {p.budgetBE > 0 ? `${p.budgetBE} h` : '—'}
                  </span>
                </td>

                {/* BUREAU D'ÉTUDE RÉEL */}
                <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                  {(() => {
                    const pctBE = p.budgetBE > 0 ? (p.consumedBE / p.budgetBE) * 100 : 0;
                    return (
                      <span style={{ fontSize: '0.85rem', fontWeight: '700', color: pctBE > 100 ? '#ef4444' : '#334155' }}>
                        {p.consumedBE > 0 ? `${p.consumedBE.toFixed(0)} h` : '—'}
                      </span>
                    );
                  })()}
                </td>

                {/* ACHATS */}
                <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.88rem', fontWeight: '600', color: '#475569' }}>
                    {fmtK(p.totalAchats)}
                  </span>
                </td>

                {/* CA FACTURÉ */}
                <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.88rem', fontWeight: '600', color: p.totalFacture > 0 ? '#7c3aed' : '#cbd5e1' }}>
                    {p.totalFacture > 0 ? fmtK(p.totalFacture) : '—'}
                  </span>
                </td>

                {/* RESTE À FACTURER */}
                <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                  {p.resteAFacturer != null ? (
                    <span style={{
                      fontSize: '0.85rem', fontWeight: '700',
                      color: p.resteAFacturer < 0 ? '#ef4444' : p.resteAFacturer === 0 ? '#10b981' : '#475569'
                    }}>
                      {p.resteAFacturer < 0 ? `${fmtK(Math.abs(p.resteAFacturer))} ⚠️` : fmtK(p.resteAFacturer)}
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.82rem', color: '#cbd5e1' }}>—</span>
                  )}
                </td>

                {/* MARGE */}
                <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                  {p.margePercent != null ? (
                    <span style={{
                      fontSize: '0.88rem', fontWeight: '800',
                      color: p.margePercent >= 25 ? '#16a34a' : p.margePercent >= 10 ? '#d97706' : '#ef4444'
                    }}>
                      +{p.margePercent.toFixed(1)}%
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.82rem', color: '#cbd5e1' }}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
  );
}
